// The website host: one script tag puts the companions on a page.
//
//   <script defer src="/companions/companions.min.js" data-companions="bit,chrome"></script>
//
// It reads its options from its own tag (or Companions.start(options) from a page's script), lays
// a click-through overlay over the page, finds spots on the page's own elements (the page world)
// and offers a settings sheet. window.Companions is the page's handle on it.
//
// Which options win, from weakest to strongest: the built-in defaults, the page's options (the
// tag or start()), the visitor's own saved choices. The visitor's choices are kept only in this
// browser and only once they change something.
import { createRuntime } from '../runtime/index.js';
import { createBrowserInput } from '../runtime/input.js';
import { ACTIVITY, GATHERINGS } from '../engine/activity.js';
import { SKINS, skinsFor, lookFor, holdSkins } from '../engine/skins.js';
import { OUTFITS } from '../engine/outfits.js';
import { SIZES, holdIdle, cast as onScreen } from '../engine/companion.js';   // (the engine's list; the runtime's is `cast` below)
import { baseOf } from '../engine/travel.js';
import { paintBubble } from '../engine/speech.js';
import { tick } from '../engine/core.js';
import { wave as waveAct } from '../engine/acts.js';
import { drawPortrait as paintPortrait } from '../ui/portrait.js';
import { GROUP_ORDER } from '../ui/content.js';
import { swatchBackground } from '../ui/swatches.js';
import { catalog, load } from './designs.js';
import { loadLanguage } from './languages.js';
import { frameClock } from './clock.js';
import { namespaced, forget, prefixOf } from './storage.js';
import { createOverlay } from './overlay.js';
import { loadMenu } from './menu.js';
import { createPageWorld } from './page-world.js';
import { createStill } from './still.js';

export const VERSION = '0.1.0';
const MOTIONS = ['moving', 'still', 'hidden'];
const DEFAULTS = { size: 4, phoneSize: 3, activity: 'calm', gatherings: 'normal' };
// Who comes when the page doesn't say: Bit, the app's face (or the first companion there is).
const FACE = 'bit';
const slot = Symbol.for('companions.embed');

const warned = new Set();
function warn(message) {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn('Companions: ' + message);
}

// The tag's data attributes, as start() options: data-companions="bit,chrome" data-size="5"
// data-activity="calm" data-gatherings="normal" data-language="de" data-settings-button="false"
// data-storage="mysite" (data-autostart="false": the page calls Companions.start() itself).
function tagOptions(tag) {
  const d = tag?.dataset;
  if (!d) return {};
  const o = {};
  if (d.companions !== undefined) o.companions = d.companions.split(',').map(id => id.trim()).filter(Boolean);
  if (d.size) o.size = Number(d.size);
  if (d.activity) o.activity = d.activity;
  if (d.gatherings) o.gatherings = d.gatherings;
  if (d.language) o.language = d.language;
  if (d.scroll) o.scroll = d.scroll;
  if (d.settingsButton === 'false') o.settingsButton = false;
  if (d.storage) o.storage = d.storage;
  if (tag.nonce) o.nonce = tag.nonce;
  return o;
}

// A phone: a narrow screen or a touch screen as the main pointer.
const isPhone = () => matchMedia('(max-width: 600px), (pointer: coarse)').matches;

// The world decides whether this element actually has a usable spot; the runtime checks room
// and the other companions. Missing selectors are page mistakes; off-screen spots are normal.
function startingSpots(spots, world, ids) {
  const result = {};
  for (const [id, spot] of Object.entries(spots || {})) {
    if (!ids.has(id)) { warn(`no companion called ${id} in spots; ignored.`); continue; }
    let el = spot?.on;
    if (typeof el === 'string') {
      try { el = document.querySelector(el); }
      catch { warn(`spots.${id}.on "${spot.on}" is not a valid selector; ignored.`); continue; }
      if (!el) { warn(`spots.${id}.on "${spot.on}" matches nothing; ignored.`); continue; }
    }
    if (!(el instanceof Element)) continue;
    result[id] = { surfaceId: world.startIds(el), at: Number.isFinite(spot.at) ? Math.max(0, Math.min(1, spot.at)) : 0.5,
      rest: spot.rest === 'sit' ? 'sit' : 'stand' };
  }
  return result;
}

// Who's on screen, the size and the levels, after the page's options and the visitor's choices.
function choose(options, ids, store, phone) {
  const listed = new Set(ids), unknown = new Set();
  const known = list => (Array.isArray(list) ? list : []).filter(id => listed.has(id) || (unknown.add(String(id)), false));
  const page = phone ? (options.phone?.companions ? known(options.phone.companions) : known(options.companions).slice(0, 1))
    : known(options.companions);
  if (unknown.size) warn(`no companion called ${[...unknown].join(', ')}; ignored.`);
  let saved = null;
  try { saved = JSON.parse(store.get('onScreen', 'null')); } catch { /* the page's choice */ }
  const companions = Array.isArray(saved) ? saved.filter(id => listed.has(id)) : page.length ? page : listed.has(FACE) ? [FACE] : ids.slice(0, 1);

  const fallback = phone ? DEFAULTS.phoneSize : DEFAULTS.size, wanted = phone ? options.phone?.size : options.size;
  let size = Number(wanted ?? fallback);
  if (!Number.isFinite(size)) { warn(`size "${wanted}" is not a number; using ${fallback}.`); size = fallback; }
  const savedSize = store.get('size');
  if (savedSize !== null && Number.isFinite(Number(savedSize))) size = Number(savedSize);

  const level = (key, levels, saved = store.get(key)) => {
    const wanted = options[key];
    if (wanted !== undefined && !(wanted in levels)) warn(`${key} "${wanted}" is not one of ${Object.keys(levels).join(', ')}; ignored.`);
    return saved in levels ? saved : wanted in levels ? wanted : DEFAULTS[key];
  };
  // The gatherings level is only ever the page's (the settings don't offer it any more), so a
  // visitor's old choice is left alone.
  return { companions, size, activity: level('activity', ACTIVITY), gatherings: level('gatherings', GATHERINGS, null) };
}

// The page world owns the visible bottom of the screen (page-world.js: the visual viewport, a phone's
// keyboard and safe areas, a fixed bar along the bottom, where nothing passes under it). The embed
// adds what is its own: the opener's corner stays free, and the floor holds still while the visitor
// types on a phone (the keyboard would lift companions onto the field).
function embedWorld(world, { corner, typing }) {
  let source = null, mode = false, out = null, held = null;
  return {
    snapshot() {
      const s = world.snapshot(), hold = typing();
      if (!s.floor) return s;                            // destroyed
      if (s === source && mode === hold) return out;
      source = s; mode = hold;
      const x1 = Math.max(s.floor.x1, corner());
      let floor = { ...s.floor, x1, rest: s.floor.rest?.map(([a, b]) => [Math.max(a, x1), b]).filter(([a, b]) => b > a) };
      if (hold) floor = held ??= floor; else held = null;
      out = { ...s, floor };
      return out;
    },
    subscribe: changed => world.subscribe(changed),
    clearFloor: scale => world.clearFloor(scale),
    destroy: () => world.destroy(),
  };
}

// Moving skins, and what a drawing does by itself (swinging legs, a floater's bob), held still or let go.
const holdStill = on => { holdSkins(on); holdIdle(on); };

// A field the visitor types in (not a button, a box to tick or our own settings sheet).
const NOT_TYPING = new Set(['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image', 'hidden']);
const typesIn = node => node instanceof Element && !node.matches('companions-overlay')
  && (node.isContentEditable || node.matches('textarea') || (node.matches('input') && !NOT_TYPING.has(node.type)));

function install() {
  const events = new Map();
  let current = null, starting = null, destroying = null, lastOptions = {};
  const tag = document.currentScript;               // the bundle's own <script>; null in an ES module
  const fromTag = tagOptions(tag);

  function emit(name, value) {
    for (const fn of events.get(name) || []) {
      try { fn(value); } catch (e) { console.error(e); }
    }
  }
  const need = () => {
    if (!current) throw new Error('Companions are not running: await Companions.start() first.');
    return current;
  };

  async function boot(options) {
    if (document.readyState === 'loading') await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
    const prefix = prefixOf(options.storage), store = namespaced('localStorage', prefix), visit = namespaced('sessionStorage', prefix);
    // Every companion is listed; only those on screen load now (their designs and moves), the others
    // when they're put on screen, and all designs when a menu needs their pictures.
    const list = await catalog(), names = new Map(list.map(c => [c.id, c.name]));
    const phone = isPhone(), reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const chosen = choose(options, list.map(c => c.id), store, phone);
    const scroll = ['fall', 'ride'].includes(options.scroll) ? options.scroll : 'fall';
    if (options.scroll !== undefined && options.scroll !== scroll) warn(`scroll "${options.scroll}" is not one of fall, ride; using fall.`);
    const designs = chosen.companions.length ? await load(chosen.companions, { moves: true }) : {};
    // Reduced motion starts still (no moves of their own, no parties or games, no moving skins)
    // until the visitor picks Moving in the sheet; that choice, like Still and Hidden, lasts the visit.
    let motion = MOTIONS.includes(visit.get('motion')) ? visit.get('motion') : reduced ? 'still' : 'moving';
    let typing = false, regions = [], sheet = null, menu = null, sheetWanted = false, gone = false;
    const holds = new Set();
    const listeners = new AbortController(), signal = listeners.signal;

    const overlay = createOverlay({ nonce: options.nonce, button: options.settingsButton !== false,
      nameOf: id => names.get(id) || id, onOpen: opener => openSettings(opener) });
    const pageWorld = createPageWorld({ exclude: [overlay.host], riders: scroll === 'ride' ? () => onScreen.flatMap(me =>
      [me.anchor, me.dest].filter(Boolean).flatMap(spot => [spot.ledge.id, baseOf(spot).id])) : null });
    const initialSpots = startingSpots(phone ? options.phone?.spots ?? options.spots : options.spots, pageWorld, new Set(names.keys()));
    const world = embedWorld(pageWorld, { corner: overlay.corner, typing: () => typing });
    // Still (still.js) places companions itself wherever the runtime would move them on its own.
    const still = createStill({ on: () => motion === 'still', ride: scroll === 'ride' });
    holdStill(motion !== 'moving');
    // The language: 'auto' (unless the visitor chose one in the sheet) takes the page's own first
    // (the option or data-language, else <html lang>), then the browser's. Each language's texts
    // are a file of their own, loaded only when it's the one in use.
    const languages = { load: loadLanguage,
      preferred: () => [options.language, document.documentElement.lang, ...(navigator.languages || [])].filter(Boolean) };
    let runtime;
    try {
      // Newcomers start on the page's own spots (the floor only when there are none); in Still
      // without the puff and the hop.
      runtime = await createRuntime({
        canvas: overlay.canvas, designs, catalog: list, load, world, storage: store, clock: frameClock(30), languages,
        input: createBrowserInput({ target: overlay.host }),
        options: { ...chosen, looks: options.looks, initialSpots, checkInitialSpots: true, ride: scroll === 'ride',
          alive: motion === 'moving', startOn: 'surfaces', place: still.place },
      });
    } catch (e) {
      holdStill(false); world.destroy(); overlay.destroy();
      throw e;
    }
    const { all, cast, free } = runtime.inspect;

    // Drawing and actions run unless something holds them: a hidden tab, the page (pause()),
    // or the visitor's Hidden.
    const pauseFor = (reason, on) => {
      if (on) holds.add(reason); else holds.delete(reason);
      if (holds.size) runtime.pause(); else runtime.resume();
    };
    const applyAlive = () => {
      const alive = motion === 'moving' && !typing;
      if (runtime.getOptions().alive !== alive) runtime.setOptions({ alive });
    };
    // Their own lives stop or go on first (the runtime settles each where it is), then Still places
    // those that can't stay where they are, or Moving brings back those Still kept out of sight.
    function setMotion(next) {
      if (!MOTIONS.includes(next)) throw new Error(`motion is one of ${MOTIONS.join(', ')}.`);
      if (next === motion) return;
      motion = next;
      visit.set('motion', next);
      motionHolds();
      applyAlive();
      if (next === 'still') still.enter();
      else if (next === 'moving') still.leave();
      sheet?.refresh();
      emit('change', { type: 'options' });
    }

    // The page's hold (Companions.hold): it keeps a companion where it is, doing nothing new of its
    // own (a bubble over it, say), until the page lets go. A run of the page's own keeps it (or its
    // wave's, which then stays); anything else that moves it ends the hold: the visitor picks it up
    // or changes Motion, its spot goes away, it leaves the page, destroy().
    const kept = new Set(), mine = new Map();       // on hold; companion -> the page's latest run of it
    const ours = me => { const run = mine.get(me); return !!run && !run.done && run.gen === me.gen; };
    const onHold = me => kept.has(me) && ours(me);
    function pageRun(me, first = null) {
      const run = { done: false };
      runtime.commands.run(me, async g => {
        try {
          if (first) await first(g);
          while (kept.has(me)) await tick(g);
        } finally { run.done = true; }
      });
      run.gen = me.gen;
      mine.set(me, run);
    }
    function keep(me, on) {
      if (!on) { kept.delete(me); return false; }
      if (!cast.includes(me)) return false;
      if (ours(me)) { kept.add(me); return true; }    // on hold already, or waving: then it stays
      if (!free(me)) return false;
      kept.add(me);
      pageRun(me);
      return true;
    }
    function wave(me) {
      if (!free(me) && !onHold(me)) return false;
      pageRun(me, g => waveAct(g));
      return true;
    }
    const regionOf = r => { const me = all[r.id]; return { ...r, asleep: !!me.sleeping, free: free(me), onHold: onHold(me) }; };

    // Show me from the sheet (runtime/showme.js). Moving: it plays. Still: it plays, with the
    // moving skins and the drawings' own motion let go for it; then they hold still again, each
    // where it can stay (still.js places any that can't). Hidden: they show for it (the others
    // hold still meanwhile, as in Still) and are hidden again after. Outside Moving a visitor
    // leaves in a puff instead of walking off.
    let showing = 0;
    function motionHolds() {
      holdStill(motion !== 'moving' && !showing);
      overlay.setHidden(motion === 'hidden' && !showing);
      pauseFor('hidden', motion === 'hidden' && !showing);
    }
    async function showMe(id, key, { with: partner = null } = {}) {
      showing++;
      motionHolds();
      try {
        return await runtime.showMe.run(id, key, { with: partner, puff: motion !== 'moving' });
      } finally {
        showing--;
        motionHolds();
        if (!showing && motion === 'still') still.enter();
      }
    }

    // The opener shows the first companion on screen (or the first there is).
    const paintOpener = () => { if (overlay.portrait) paintPortrait(overlay.portrait, cast[0] || Object.values(all)[0], { room: 2 }); };
    let size = runtime.getOptions().size;
    const offs = [
      runtime.on('click', detail => emit('click', detail)),
      runtime.on('change', change => {
        // The runtime keeps the levels and who's on screen; the size is kept here.
        if (change.type === 'options' && runtime.getOptions().size !== size) { size = runtime.getOptions().size; store.set('size', String(size)); }
        if (change.type !== 'options') paintOpener();
        if (change.type === 'onScreen' && !change.on) { kept.delete(all[change.id]); still.removed(all[change.id]); }
        emit('change', change);
      }),
      runtime.on('hitRegions', list => { regions = list.map(regionOf); overlay.setRegions(regions); emit('hitRegions', regions); }),
      runtime.on('frame', now => {
        for (const me of kept) if (!ours(me)) kept.delete(me);   // something else moved it: the hold is over
        still.frame(now);
        emit('frame', now);
      }),
    ];
    paintOpener();

    // Still brings back companions that lost their spot once the page has stopped scrolling.
    document.addEventListener('scroll', () => still.scrolled(), { capture: true, passive: true, signal });
    globalThis.visualViewport?.addEventListener('scroll', () => still.scrolled(), { passive: true, signal });
    globalThis.visualViewport?.addEventListener('resize', () => still.scrolled(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => pauseFor('tab', document.visibilityState === 'hidden'), { signal });
    // Any element with data-companions-settings opens the sheet, including ones added later.
    document.addEventListener('click', e => {
      const opener = e.target.closest?.('[data-companions-settings]');
      if (!opener) return;
      e.preventDefault();
      openSettings(opener);
    }, { signal });

    // The settings sheet and the menu in it are a part of their own (menu.js). It loads when the
    // visitor reaches for the settings (a pointer or a finger on the button or on an element with
    // data-companions-settings, or the keyboard's focus there), so it's here by the click; a visitor
    // who never does never loads it. The sheet is made the first time it opens.
    const menuPart = () => (menu ??= loadMenu().catch(e => { menu = null; throw e; }));
    const reach = () => { menuPart().catch(() => { /* said when it's opened */ }); };
    for (const type of ['pointerover', 'pointerdown', 'focusin']) {
      overlay.opener?.addEventListener(type, reach, { signal, passive: true });
      document.addEventListener(type, e => { if (e.target.closest?.('[data-companions-settings]')) reach(); }, { signal, passive: true });
    }
    function openSheet(opener) {
      sheetWanted = true;
      menuPart().then(({ createSheet }) => {
        if (gone || !sheetWanted) return;             // closed (or gone) while it loaded
        sheet ??= createSheet({ root: overlay.root, runtime, nonce: options.nonce, motion: () => motion, setMotion, showMe,
          onToggle: open => { sheetWanted = open; emit('settings', { open }); } });
        sheet.open(opener);
      }).catch(e => { sheetWanted = false; console.error('Companions: the settings could not be opened.', e); });
    }
    function closeSheet() {
      sheetWanted = false;
      sheet?.close();
    }
    // On a phone they keep still while the visitor types (the keyboard takes half the screen).
    if (phone) {
      const setTyping = on => { if (typing !== on) { typing = on; applyAlive(); } };
      document.addEventListener('focusin', e => setTyping(typesIn(e.target)), { signal });
      document.addEventListener('focusout', e => setTyping(typesIn(e.relatedTarget)), { signal });
      setTyping(typesIn(document.activeElement));
    }

    overlay.setHidden(motion === 'hidden');
    still.attach(runtime);                              // Still: no opening hop; those without a spot stay out of sight
    if (motion === 'hidden') for (const me of cast) runtime.commands.run(me, async () => {});   // no opening hop
    if (motion === 'hidden') holds.add('hidden');
    if (document.visibilityState === 'hidden') holds.add('tab');
    if (!holds.size) runtime.start();

    return {
      runtime,
      scroll,
      get motion() { return motion; },
      get regions() { return regions; },
      setMotion, pauseFor, keep, wave, showMe,
      openSheet, closeSheet,
      async destroy() {
        gone = true;
        listeners.abort();
        sheet?.destroy();
        for (const off of offs) off();
        await runtime.destroy();
        still.destroy(); world.destroy(); overlay.destroy(); holdStill(false);
      },
    };
  }

  function start(options = {}) {
    if (starting && !destroying) return starting;     // already running, or on its way
    lastOptions = { ...fromTag, ...options };
    starting = (destroying || Promise.resolve()).then(() => boot(lastOptions)).then(instance => {
      current = instance;
      emit('start', api);
      return api;
    }, e => { starting = null; throw e; });
    return starting;
  }

  function destroy() {
    if (destroying) return destroying;
    if (!starting) return Promise.resolve();
    const was = starting;
    destroying = (async () => {
      try { await was; } catch { return; }           // it never started
      const instance = current;
      current = null;
      await instance.destroy();
      emit('destroy');
    })().finally(() => { if (starting === was) starting = null; destroying = null; });
    return destroying;
  }

  function openSettings(opener) {
    if (current) current.openSheet(opener instanceof Element ? opener : null);
    else starting?.then(() => openSettings(opener), () => {});
  }

  // A companion whose design is here (Companions.load() brings them; one on screen always is).
  const loaded = id => {
    const { runtime } = need();
    if (!runtime.getCompanions().includes(id)) throw new Error('No companion called ' + id);
    if (!runtime.isReady(id, { moves: false })) throw new Error(`${id} isn't loaded yet: await Companions.load(['${id}']) first.`);
    return id;
  };
  const instance = id => need().runtime.inspect.all[loaded(id)];

  const api = {
    version: VERSION,
    start, destroy,
    // The page's own pause (a video playing, say): everything holds still until resume().
    pause() { current?.pauseFor('page', true); },
    resume() { current?.pauseFor('page', false); },
    // Forgets the visitor's choices and starts again with the page's options.
    async reset() {
      const options = lastOptions;
      await destroy();
      forget(prefixOf(options.storage));
      return start(options);
    },
    openSettings, closeSettings() { current?.closeSheet(); },
    on(name, fn) {
      if (!events.has(name)) events.set(name, new Set());
      events.get(name).add(fn);
      return () => events.get(name)?.delete(fn);
    },

    // Settings, as the runtime keeps them (runtime/README.md), plus the Pause choice as `motion`.
    setOnScreen: (id, on) => need().runtime.setOnScreen(id, on),
    setCompanion: (id, settings) => need().runtime.setCompanion(loaded(id), settings),
    getCompanion: id => need().runtime.getCompanion(loaded(id)),
    getCompanions: () => need().runtime.getCompanions(),
    getOptions: () => ({ ...need().runtime.getOptions(), motion: need().motion, scroll: need().scroll }),
    setOptions(next = {}) {
      const { motion, alive, ...rest } = next, run = need();
      if (motion !== undefined) run.setMotion(motion);
      else if (typeof alive === 'boolean') run.setMotion(alive ? 'moving' : 'still');
      if (Object.keys(rest).length) run.runtime.setOptions(rest);
    },

    // What a page's own menu needs, without importing the engine. Every companion is listed (id,
    // name, group); its description, portrait, skins, outfits and settings need its design: load()
    // brings them (all by default).
    designs() {
      const { runtime } = need(), on = new Set(runtime.inspect.cast.map(me => me.design.id));
      return runtime.catalog().map(c => ({ ...c, onScreen: on.has(c.id) }));
    },
    load: (ids = null) => need().runtime.ready(ids ?? need().runtime.getCompanions(), { moves: false }),
    groups() {
      const groups = [...new Set(api.designs().map(d => d.group))];
      return groups.sort((a, b) => (GROUP_ORDER.indexOf(a) + 1 || 99) - (GROUP_ORDER.indexOf(b) + 1 || 99));
    },
    // Its skins: shared ones first, then its own; swatch is a CSS background (element.style.background).
    skins(id) {
      const { design } = instance(id);
      return skinsFor(design).map(key => {
        const look = lookFor(design, key);
        return { key, label: look.label, own: !SKINS[key], moving: !!look.fx, swatch: swatchBackground(look) };
      });
    },
    // The outfits it can wear, by slot (only the slots it has).
    outfits: id => Object.fromEntries((instance(id).design.outfits || []).map(slot =>
      [slot, Object.entries(OUTFITS[slot]).map(([key, label]) => ({ key, label }))])),
    sizes: () => Object.keys(SIZES).map(Number).sort((a, b) => a - b).map(step => ({ step, label: SIZES[step] })),
    // Draws it, still, as big as fits, standing on the canvas's bottom edge (in its current looks).
    drawPortrait(canvas, id) { paintPortrait(canvas, instance(id)); },
    // A speech bubble in the companions' pixel font, tail down, sized to fit: for a page's own
    // clickable bubble. Returns its size in CSS pixels (the canvas's style is set to it).
    drawBubble(canvas, text, { scale = 2 } = {}) {
      const dpr = globalThis.devicePixelRatio || 1, size = paintBubble(canvas, String(text), scale, dpr);
      canvas.style.width = size.width + 'px'; canvas.style.height = size.height + 'px';
      return size;
    },
    // Where they are now, in viewport pixels, and how they are: [{ id, x, y, width, height, asleep,
    // free, onHold }] (also the hitRegions event). free: on the page, idle, awake and not picked up.
    regions: () => need().regions.map(r => ({ ...r })),
    // Waves, if it's on screen and free (or on hold: then it stays on hold). True when it does.
    wave: id => need().wave(instance(id)),
    // hold(id): it stays where it is and starts nothing new of its own (a bubble over it, say) until
    // hold(id, false). True when it's on hold. It lets go by itself when the visitor picks it up or
    // changes Motion, when its spot goes away, when it leaves the page and on destroy().
    hold: (id, on = true) => need().keep(instance(id), on !== false),
    // Show me (runtime/showme.js): what it can show, [{ key, action, partner }], and who could be the
    // partner of one that needs one (ids, best first).
    shows: id => need().runtime.showMe.list(loaded(id)),
    partners: (id, key) => need().runtime.showMe.partners(loaded(id), key),
    // It does that now, once, with the partner `with` if it needs one (one not on the page visits for
    // it). A promise: true once it played, false when it couldn't. In Still it plays and they hold
    // still again; in Hidden they show for it and are hidden again.
    showMe: (id, key, options) => need().showMe(id, key, options),
  };

  if (tag && tag.dataset.autostart !== 'false') start().catch(e => console.error('Companions could not start:', e));
  return api;
}

// One copy per page: a second copy of the script only says so, once.
const existing = globalThis[slot];
if (existing) {
  if (!existing.warned) { existing.warned = true; console.warn('Companions: this page loads the companions script more than once; only the first copy runs.'); }
} else {
  globalThis[slot] = { api: install(), warned: false };
  globalThis.Companions = globalThis[slot].api;
}
const Companions = globalThis[slot].api;
export default Companions;
